<template>
  <v-container fluid class="pa-0">
    <banner
      :title="$t('SettingsPageTitle')"
      :description="$t('SettingsPageDescription')"
    />
    <v-container>
      <v-row justify="center">
        <v-col cols="12" lg="6">
          <v-card 
            outlined
            class="mx-auto mb-10 custom-radius"
            max-width="760"
          >
            <v-card-title v-text="$t('Notifications')"></v-card-title>
            <v-divider></v-divider>
            <v-switch 
              inset
              :value="hasAllowEmailNotifications"
              :input-value="hasAllowEmailNotifications"
              class="mx-4"
              :label="$t('AllowNotifications')"
              @change="submitAllowEmailNotificationSettings"
            ></v-switch>
          </v-card>
          <v-card 
            outlined
            id="pricing"
            class="mx-auto mb-10 custom-radius"
            max-width="760"
          >
            <v-card-title v-text="$t('SessionPrices')"></v-card-title>
              <v-divider></v-divider>
                <v-card-text class="pt-5">
                  <div class="subtitle-1 mb-4">{{ $t('MinimumSessionPrice') }} <span class="font-weight-medium">{{ minSessionPrice }}$</span></div>
                  <div class="subtitle-3 mb-4">{{ $t('SessionPriceLimit') }} <span class="font-weight-medium">{{sessionPriceChangeLimit}}</span></div>
                  <v-row>
                    <v-col cols="12" md="6">
                      <v-text-field
                        rounded
                        outlined
                        hide-details
                        type="number"
                        :min="minSessionPrice"
                        :label="$t('WeekDayPrice')"
                        v-model="weekdayPrice"
                        :error="weekdayPrice < minSessionPrice"
                      ></v-text-field>
                      <div class="subtitle-3 float-right mr-3"><span class="font-weight-medium"> {{weekdayChangesLeft}}</span> {{$t('SessionPriceLimitLeft')}}</div>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field
                        rounded
                        outlined
                        hide-details
                        type="number"
                        :min="minSessionPrice"
                        :label="$t('WeekendDayPrice')"
                        v-model="weekendPrice"
                        :error="weekendPrice < minSessionPrice"
                      ></v-text-field>
                      <div class="subtitle-3 float-right mr-3"><span class="font-weight-medium">{{weekendChangesLeft}}</span> {{$t('SessionPriceLimitLeft')}}</div>
                    </v-col>
                  </v-row>
                  <div class="text-center">
                    <v-btn
                      rounded
                      depressed
                      color="primary"
                      class="mt-2"
                      :disabled="!isPriceFormValid"
                      @click="saveTutorPriceSettings"
                      v-text="$t('Save')"
                    ></v-btn>
                  </div>
              </v-card-text>
          </v-card>
          <!-- TIMEZONE -->
          <v-card 
            outlined
            id="timezone"
            class="mx-auto mb-10 custom-radius"
            max-width="760"
          >
            <v-card-title v-text="$t('Timezone')"></v-card-title>
            <v-divider></v-divider>
            <v-card-text class="pt-6">
              <v-combobox
                outlined
                rounded
                hide-details
                class="mb-3"
                :disabled="loadingTimezones"
                :items="timezones"
                :label="$t('SelectYourTimezone')"
                :value="user.timezoneId"
                @change="changeTimezone"
              ></v-combobox>
            </v-card-text>
          </v-card>
          <!-- LANGUAGE -->
          <!-- <v-card 
            outlined
            class="mx-auto mb-10 custom-radius"
            max-width="760"
          >
            <v-card-title v-text="$t('Language')"></v-card-title>
            <v-divider></v-divider>
            <v-card-text class="pt-6">
              <v-select
                outlined
                rounded
                hide-details
                class="mb-3"
                :items="languages"
                item-text="label"
                item-value="id"
                :label="$t('Language')"
                :value="user.preferedLanguage"
                @change="changePeferedLanguage"
              ></v-select>
            </v-card-text>
          </v-card> -->
          <v-card 
            outlined
            class="mx-auto mb-10 custom-radius"
            max-width="760"
          >
            <v-card-title v-text="$t('ChangePasswordTitle')"></v-card-title>
            <v-divider></v-divider>
            <v-card-text class="pt-6">
              <v-text-field
                rounded
                outlined
                required
                validate-on-blur
                type="password"
                :rules="passwordRules.oldPassword"
                v-model="passwordForm.oldPassword"
                :label="$t('OldPassword')"
              ></v-text-field>
              <v-text-field
                rounded
                outlined
                required
                validate-on-blur
                type="password"
                :rules="passwordRules.password"
                v-model="passwordForm.password"
                :label="$t('Password')"
              ></v-text-field>
              <v-text-field
                rounded
                outlined
                required
                validate-on-blur
                type="password"
                :rules="passwordRules.confirmPassword"
                v-model="passwordForm.confirmPassword"
                :label="$t('ConfirmPassword')"
              ></v-text-field>
              <div class="text-center">
                <v-btn 
                  rounded 
                  depressed 
                  color="primary" 
                  @click="changePassword"
                  :disabled="changingPasswordRequest || !isPasswordFormValid"
                  :loading="changingPasswordRequest"
                >{{ $t('Save') }}</v-btn>
              </div>
            </v-card-text>
          </v-card>
          <!-- Deactivate account -->
          <v-card 
            outlined
            class="mx-auto mb-10 custom-radius"
            max-width="760"
          >
            <v-card-title class="d-flex justify-space-between">
              <span v-text="$t('DeactivateAccount')"></span>
              <v-btn rounded depressed color="secondary" @click="accountDeactivationConfirmationSheet = true">{{ $t('Deactivate') }}</v-btn>
            </v-card-title>
          </v-card>
          <!-- Delete account -->
          <v-card 
            outlined
            class="mx-auto mb-10 custom-radius"
            max-width="760"
          >
            <v-card-title class="d-flex justify-space-between">
              <span v-text="$t('DeleteAccount')"></span>
              <v-btn rounded depressed color="secondary" @click="deleteAccountConfirmationSheet = true">{{ $t('Delete') }}</v-btn>
            </v-card-title>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
    <!-- Deactive account confirmation sheet -->
    <v-bottom-sheet v-model="accountDeactivationConfirmationSheet">
      <v-sheet class="text-center pa-2">
        <v-row justify="center">
          <v-col cols="12" md="10" lg="4">
            <div class="title" v-text="$t('DeactiveAccountConfirmationNotification')"></div>
            <div class="subtitle py-4" v-text="$t('DeactivationConfirmationClarification')"></div>
            <v-btn
              text
              color="secondary"
              @click="deactivateAccountReasonEventHandler"
              v-text="$t('Yes')"
            ></v-btn>
            <v-btn
              text
              color="primary"
              @click="accountDeactivationConfirmationSheet = !accountDeactivationConfirmationSheet"
              v-text="$t('No')"
            ></v-btn>
          </v-col>
        </v-row>
      </v-sheet>
    </v-bottom-sheet>
    <!-- Reason for deactivating account dialog -->
    <v-dialog v-model="reasonForAccountDeactivationDialog" max-width="420px">
      <v-card>
        <div class="pa-4 pa-lg-6">
          <v-card-title class="pa-0" v-text="$t('ReasonForDeactivatingAccount')"></v-card-title>
          <v-radio-group v-model="reasonForAccountDeactivation">
            <v-radio
              v-for="(reason, index) in reasons"
              :key="index"
              :label="reason.text"
              :value="reason.text"
            ></v-radio>
            <v-radio
              label="Other"
              :value="0"
            ></v-radio>
          </v-radio-group>
          <v-textarea
            rounded
            outlined
            no-resize
            height="100"
            v-model="reasonForAccountDeactivationMessage"
            v-if="reasonForAccountDeactivation === 0"
          ></v-textarea>
          <div class="d-flex align-center justify-space-between">
            <v-btn small rounded depressed color="primary" @click="deactivateAccount">{{ $t('Deactivate') }}</v-btn>
            <v-btn small rounded depressed @click="reasonForAccountDeactivationDialog = false">{{ $t('Close') }}</v-btn>
          </div>
        </div>
      </v-card>
    </v-dialog>
    <!-- Delete account confirmation sheet -->
    <v-bottom-sheet v-model="deleteAccountConfirmationSheet">
      <v-sheet class="text-center pa-2">
        <v-row justify="center">
          <v-col cols="12" md="10" lg="4">
            <div class="title" v-text="$t('DeleteAccountConfirmationNotification')"></div>
            <div class="subtitle py-4" v-text="$t('DeletionConfirmationClarification')"></div>
            <v-btn
              text
              color="secondary"
              @click="deleteAccountReasonEventHandler"
              v-text="$t('Yes')"
            ></v-btn>
            <v-btn
              text
              color="primary"
              @click="deleteAccountConfirmationSheet = !deleteAccountConfirmationSheet"
              v-text="$t('No')"
            ></v-btn>
          </v-col>
        </v-row>
      </v-sheet>
    </v-bottom-sheet>
    <!-- Reason for delete account dialog -->
    <v-dialog v-model="reasonForDeleteAccountDialog" max-width="420px">
      <v-card>
        <div class="pa-4 pa-lg-6">
          <v-card-title class="pa-0" v-text="$t('ReasonForDeletingAccount')"></v-card-title>
          <v-radio-group v-model="reasonForDeleteAccount">
            <v-radio
              v-for="(reason, index) in reasons"
              :key="index"
              :label="reason.text"
              :value="reason.text"
            ></v-radio>
            <v-radio
              label="Other"
              :value="0"
            ></v-radio>
          </v-radio-group>
          <v-textarea
            rounded
            outlined
            no-resize
            height="100"
            v-model="reasonForDeleteAccountMessage"
            v-if="reasonForDeleteAccount === 0"
          ></v-textarea>
          <div class="d-flex align-center justify-space-between">
            <v-btn small rounded depressed color="primary" @click="deleteAccount">{{ $t('Delete') }}</v-btn>
            <v-btn small rounded depressed @click="reasonForDeleteAccountDialog = false">{{ $t('Close') }}</v-btn>
          </div>
        </div>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
  import Banner from '@/components/pageBanner'
  import { mapGetters } from 'vuex'

  export default  {
    name: 'tutorSettings',
    data() {
      const defaultPasswordForm = Object.freeze({
        oldPassword: '',
        password: '',
        confirmPassword: '',
      })
      return {
        passwordForm: Object.assign({}, defaultPasswordForm),
        passwordRules: {
          oldPassword: [val => (val || '').length > 0],
          password: [                        
            val => {
              const pattern = /^(?=.*\d)(?=.*[!@#$%^&*])(?=.*[a-z])(?=.*[A-Z]).{8,}$/
              return pattern.test(val) || 'Weak password'
            }
          ],
          confirmPassword: [
            val => (val || '').length > 0,
            val => val === this.passwordForm.password
          ]
        },
        changingPasswordRequest: false,
        loading: true,
        weekdayPrice: null,
        weekendPrice: null,
        weekdayCount: null,
        weekendCount: null,
        weekdayChangesLeft: null,
        weekendChangesLeft: null,
        sessionPriceChangeLimit: null,
        timezones: [],
        loadingTimezones: true,
        minSessionPrice: 0,
        snackbar: {
          show: true,
          message: ''
        },
        reasons: [],
        accountDeactivationConfirmationSheet: false,
        reasonForAccountDeactivationDialog: false,
        reasonForAccountDeactivation: '',
        reasonForAccountDeactivationMessage: '',
        deleteAccountConfirmationSheet: false,
        reasonForDeleteAccountDialog: false,
        reasonForDeleteAccount: '',
        reasonForDeleteAccountMessage: ''
      }
    },
    computed: {
      ...mapGetters(['user', 'languages']),
      hasAllowEmailNotifications() {
        return this.user.allowEmailNotifications
      },
      isPasswordFormValid() {
        if(this.passwordForm.oldPassword && this.passwordForm.password && this.passwordForm.confirmPassword) {
          const passwordPattern = /^(?=.*\d)(?=.*[!@#$%^&*])(?=.*[a-z])(?=.*[A-Z]).{8,}$/ 
          if(passwordPattern.test(this.passwordForm.password) && this.passwordForm.password === this.passwordForm.confirmPassword) {
            return true
          }
        }
        return false
      },
      isPriceFormValid() {
        if(this.weekdayPrice && this.weekendPrice) {
          if(this.weekdayPrice >= this.minSessionPrice && this.weekendPrice >= this.minSessionPrice) {
            return true
          }
        }
        return false
      }
    },
    components: {
      Banner
    },
    mounted() {
      this.fetchTutorPricing()
      this.fetchTimezones()
      this.fetchMinimumSessionPrice()
      this.sectionScroll()
      this.fetchReasons()
      this.loading = false
    },
    methods: {
      async fetchTimezones() {
        await this.axios.get('/System/IanaTimezones')
        .then(response => {
          this.timezones = response.data.ianaTimeZones
        })
        .catch(error => {
          this.snackbar.message = error.message
          this.$EventBus.$emit('snackbar', this.snackbar)
        })
        this.loadingTimezones = false
      },
      async fetchTutorPricing() {
        await this.axios.get('/Tutor/GetTutorSessionRate', {
          params: {
            tutorId: this.user.id,
          }
        })
        .then(response => {
          if(response.data != null && response.data !== ""){
            this.weekdayPrice = response.data.sessionRate.weekdayPrice;
            this.weekendPrice = response.data.sessionRate.weekendPrice;
            this.weekdayCount = response.data.sessionRate.weekdayCount;
            this.weekendCount = response.data.sessionRate.weekendCount;
            this.sessionPriceChangeLimit = response.data.sessionPriceChangeLimit;

            this.weekdayChangesLeft = (this.sessionPriceChangeLimit - this.weekdayCount) < 0 ? 0 : this.sessionPriceChangeLimit - this.weekdayCount;
            this.weekendChangesLeft = (this.sessionPriceChangeLimit - this.weekendCount) < 0 ? 0 : this.sessionPriceChangeLimit - this.weekendCount;
          }
            this.loading = false
        })
        .catch(error => {
          this.snackbar.message = error.message
          this.$EventBus.$emit('snackbar', this.snackbar)
        })
      },
      async fetchMinimumSessionPrice() {
        await this.axios.get('ConfigParameter/GetMinimumSessionPrice?languageId=1')
        .then(response => {
          this.minSessionPrice = response.data
        })
        .catch(error => {
          this.snackbar.message = error.message
          this.$EventBus.$emit('snackbar', this.snackbar)
        })
      },
      submitAllowEmailNotificationSettings(event) {
        if(event == null) {
          event = false
        }
        this.axios.post(`/User/EmailNotifications?userId=${this.user.id}&allow=${event}`)
        .then(response => {
          this.$store.dispatch('setUserAllowNotificationsValue', event)
          this.snackbar.message = 'Email notifications settings saved!'
          this.$EventBus.$emit('snackbar', this.snackbar)
        })
        .catch(function (error) {
          this.snackbar.message = error.response.data
          this.$EventBus.$emit('snackbar', this.snackbar)
        })
      },      
      saveTutorPriceSettings(){
        var self = this;

        const data = {
          weekdayPrice: this.weekdayPrice,
          weekendPrice: this.weekendPrice
        }
        this.axios.post('/Tutor/SaveTutorSessionRate', data)
        .then(response => {
            this.weekdayPrice = response.data.weekdayPrice
            this.weekendPrice = response.data.weekendPrice
            this.weekdayCount = response.data.weekdayCount
            this.weekendCount = response.data.weekendCount

            this.weekdayChangesLeft = (this.sessionPriceChangeLimit - this.weekdayCount) < 0 ? 0 : this.sessionPriceChangeLimit - this.weekdayCount;
            this.weekendChangesLeft = (this.sessionPriceChangeLimit - this.weekendCount) < 0 ? 0 : this.sessionPriceChangeLimit - this.weekendCount;

            this.snackbar.message = 'Pricing saved successfully'
            this.$EventBus.$emit('snackbar', this.snackbar)
        })
        .catch(function (error) {
          self.snackbar.message = error.response.data.message
          self.$EventBus.$emit('snackbar', self.snackbar)
        })
      },
      changePassword() {
        this.changingPasswordRequest = true
        const data = {
          userId: this.user.id,
          oldPassword: this.passwordForm.oldPassword,
          newPassword: this.passwordForm.password
        }
        this.axios.post('/User/ChangePassword', data)
        .then(response => {
          this.passwordForm.oldPassword = ''
          this.passwordForm.password = ''
          this.passwordForm.confirmPassword = ''
          this.snackbar.message = 'Password changed successfully'
          this.$EventBus.$emit('snackbar', this.snackbar)
          this.changingPasswordRequest = false
        })
        .catch(error => {
          this.snackbar.message = error.response.data
          this.$EventBus.$emit('snackbar', this.snackbar)
          this.changingPasswordRequest = false
        })
      },
      changePeferedLanguage(event) {
        this.axios.post(`/User/ChangePreferedLanguage?preferedLanguage=${event}`)
        .then(response => {
          const languageId = response.data
          const languageObject = {
            id: languageId,
            iso: ''
          }
          let languageIndexById = this.languages.map(lang => {
            return lang.id
          }).indexOf(languageId)
          languageObject.iso = this.languages[languageIndexById].iso639

          this.$store.dispatch('setPreferedLanguage', languageId)
          /* this.$store.dispatch('setLanguage', this.languages[languageIndexById]) */
          this.$i18n.locale = languageObject.iso
          this.$router.push({ params: { lang: languageObject.iso } })

          this.snackbar.message = 'Language changed successfully'
          this.$EventBus.$emit('snackbar', this.snackbar)
        })
        .catch(error => {
          this.snackbar.message = error.response.data
          this.$EventBus.$emit('snackbar', this.snackbar)
        })
      },
      changeTimezone(event) {
        this.axios.post(`/User/ChangeTimezoneId?timezoneId=${event}`)
        .then(response => {
          this.$store.dispatch('setTimezone', response.data)
          this.snackbar.message = 'Timezone changed successfully'
          this.$EventBus.$emit('snackbar', this.snackbar)
        })
        .catch(error => {
          this.snackbar.message = error.response.data
          this.$EventBus.$emit('snackbar', this.snackbar)
        })
      },
      deactivateAccount() {
        const reason = this.reasonForAccountDeactivation != 0 ? this.reasonForAccountDeactivation : this.reasonForAccountDeactivationMessage
        this.axios.post(`/User/DeactivateAccount?reason=${reason}`)
        .then(response => {
          this.$store.dispatch('logout')
          .then(() => {
            this.$router.push(`/${this.$i18n.locale}/`)
            this.snackbar.message = this.$i18n.t('AccountDeactivatedMessage')
            this.$EventBus.$emit('snackbar', this.snackbar)
          })
        })
        .catch(error => {
          this.snackbar.message = error.message
          this.$EventBus.$emit('snackbar', this.snackbar)
        })
      },
      deleteAccount() {
        const reason = this.reasonForDeleteAccount != 0 ? this.reasonForDeleteAccount : this.reasonForDeleteAccountMessage
        this.axios.post(`/User/DeleteAccount?reason=${reason}`)
        .then(res => {
          if(res.status === 200) {
            this.$store.dispatch('logout')
            .then(() => {
              this.$router.push(`/${this.$i18n.locale}/`)
            })
          }
        })
        .catch(error => {
          this.snackbar.message = error.message
          this.$EventBus.$emit('snackbar', this.snackbar)
        })
      },
      sectionScroll() {
        let section = this.$route.query.section
        if(section) {
          document.querySelector(`#${section}`).scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          })
        }
        return
      },
      deactivateAccountReasonEventHandler() {
        this.accountDeactivationConfirmationSheet = false
        this.reasonForAccountDeactivationDialog = true
      },
      deleteAccountReasonEventHandler() {
        this.deleteAccountConfirmationSheet = false
        this.reasonForDeleteAccountDialog = true
      },
      async fetchReasons() {
        const { data } = await this.axios.get(`/Reason/GetAll?languageId=${this.user.preferedLanguage}&amount=150&offset=0`)
        this.reasons = data.list
      }
    }
  }
</script>

<style lang="scss" scoped></style>